import { apiGet, apiPost, apiPatch } from './utils';
import type {
  ForumSection,
  ForumSectionWithActivity,
  ForumPost,
  ForumComment,
  ForumActivity,
  PagedResponse,
} from '../types';

export const forumApi = {
  // ==========================================================================
  // SECTIONS
  // ==========================================================================

  async listSections(): Promise<{ items: ForumSection[] }> {
    return apiGet<{ items: ForumSection[] }>('/forum/sections');
  },

  async getSection(slug: string): Promise<ForumSection> {
    return apiGet<ForumSection>(`/forum/sections/${slug}`);
  },

  async createSection(data: {
    title: string;
    description?: string;
    order?: number;
  }): Promise<ForumSection> {
    return apiPost<ForumSection>('/forum/sections', data);
  },

  async updateSection(
    slug: string,
    data: { title?: string; description?: string; order?: number }
  ): Promise<ForumSection> {
    return apiPatch<ForumSection>(`/forum/sections/${slug}`, data);
  },

  async getSectionsWithRecent(
    limitPerSection?: number
  ): Promise<{ items: ForumSectionWithActivity[] }> {
    return apiGet<{ items: ForumSectionWithActivity[] }>(
      '/forum/sections/with-recent',
      { limit_per_section: limitPerSection }
    );
  },

  // ==========================================================================
  // POSTS
  // ==========================================================================

  async listPosts(
    sectionSlug: string,
    cursor?: string,
    includeArchived?: boolean
  ): Promise<PagedResponse<ForumPost>> {
    return apiGet<PagedResponse<ForumPost>>(
      `/forum/sections/${sectionSlug}/posts`,
      { cursor, include_archived: includeArchived }
    );
  },

  async getPost(sectionSlug: string, postSlug: string): Promise<ForumPost> {
    return apiGet<ForumPost>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}`
    );
  },

  async createPost(
    sectionSlug: string,
    data: { title: string; body: string; sticky?: boolean; label?: string }
  ): Promise<ForumPost> {
    return apiPost<ForumPost>(`/forum/sections/${sectionSlug}/posts`, data);
  },

  async updatePost(
    sectionSlug: string,
    postSlug: string,
    data: {
      title?: string;
      body?: string;
      sticky?: boolean;
      label?: string;
      archived?: boolean;
    }
  ): Promise<ForumPost> {
    return apiPatch<ForumPost>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}`,
      data
    );
  },

  // ==========================================================================
  // COMMENTS
  // ==========================================================================

  async listComments(
    sectionSlug: string,
    postSlug: string,
    cursor?: string,
    includeArchived?: boolean
  ): Promise<PagedResponse<ForumComment>> {
    return apiGet<PagedResponse<ForumComment>>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}/comments`,
      { cursor, include_archived: includeArchived }
    );
  },

  async createComment(
    sectionSlug: string,
    postSlug: string,
    body: string
  ): Promise<ForumComment> {
    return apiPost<ForumComment>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}/comments`,
      { body }
    );
  },

  async updateComment(
    sectionSlug: string,
    postSlug: string,
    commentSlug: string,
    data: { body?: string; archived?: boolean }
  ): Promise<ForumComment> {
    return apiPatch<ForumComment>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}/comments/${commentSlug}`,
      data
    );
  },

  // ==========================================================================
  // LIKES
  // ==========================================================================

  async toggleLike(
    sectionSlug: string,
    postSlug: string,
    commentSlug: string
  ): Promise<ForumComment> {
    return apiPost<ForumComment>(
      `/forum/sections/${sectionSlug}/posts/${postSlug}/comments/${commentSlug}/like`
    );
  },

  // ==========================================================================
  // ACTIVITY
  // ==========================================================================

  async getRecentActivity(limit?: number): Promise<{ items: ForumActivity[] }> {
    return apiGet<{ items: ForumActivity[] }>('/forum/recent/activity', {
      limit,
    });
  },
};
